<template>
  <ProjectSection
    id="agendavue"
    title="AgendaVue"
    tagline="The schedule and grades app my classmates actually used."
    chip="Flutter · Dart · Figma · SOAP API"
    accent="#3b82f6"
    :icon="['fas', 'graduation-cap']"
  >
    <!-- Downloads -->
    <article v-reveal class="card span-5 dl-card">
      <h3>Used by thousands of students</h3>
      <p>Real-time schedules, assignments and grades on iOS and Android.</p>
      <div class="dl visual">
        <div class="dl-num gradient-text">30,000+</div>
        <div class="dl-sub">downloads</div>
        <div class="dl-stores">
          <span class="chip"><font-awesome-icon :icon="['fab', 'apple']" /> iOS</span>
          <span class="chip"><font-awesome-icon :icon="['fab', 'android']" /> Android</span>
        </div>
      </div>
    </article>

    <!-- Phones -->
    <article v-reveal="80" class="card span-7 screens-card">
      <h3>Designed in Figma, built in Flutter</h3>
      <p>Student-facing screens with async SOAP API integration, so grades load the moment they're posted.</p>
      <div class="screens visual visual-bleed">
        <div class="mini-phone back">
          <div class="mp-h">Schedule</div>
          <div v-for="c in schedule" :key="c.period" class="sched-row">
            <span class="period">{{ c.period }}</span>
            <span class="sched-body"><strong>{{ c.name }}</strong><small>{{ c.room }}</small></span>
          </div>
        </div>
        <div class="mini-phone front">
          <div class="mp-h">Grades</div>
          <div v-for="g in grades" :key="g.name" class="grade-row">
            <span class="grade-name">{{ g.name }}</span>
            <span class="grade-pct">{{ g.pct }}</span>
            <span class="grade-letter" :style="{ background: g.color }">{{ g.letter }}</span>
          </div>
        </div>
      </div>
    </article>

    <!-- Award -->
    <article v-reveal class="card span-12 award-card">
      <div class="award-medal">
        <font-awesome-icon :icon="['fas', 'award']" />
      </div>
      <div class="award-copy">
        <h3>AOIT Senior Excellence Award</h3>
        <p>Recognized by Wheaton High School. I improved the app by collecting feedback from the students who used it every day.</p>
      </div>
      <span class="mono award-dates">Oct 2022 – Aug 2023</span>
    </article>
  </ProjectSection>
</template>

<script>
import ProjectSection from '../ProjectSection.vue';

export default {
  name: 'AgendaVueSection',
  components: { ProjectSection },
  data() {
    return {
      schedule: [
        { period: '1', name: 'AP Calculus BC', room: 'Rm 214' },
        { period: '2', name: 'AP Computer Science', room: 'Rm 108' },
        { period: '3', name: 'English 12', room: 'Rm 302' },
        { period: '4', name: 'Physics', room: 'Lab B' },
      ],
      grades: [
        { name: 'AP Calculus BC', pct: '94.2%', letter: 'A', color: '#22c55e' },
        { name: 'AP Comp Sci', pct: '98.7%', letter: 'A', color: '#22c55e' },
        { name: 'English 12', pct: '88.5%', letter: 'B', color: '#3b82f6' },
        { name: 'Physics', pct: '91.0%', letter: 'A', color: '#22c55e' },
      ],
    };
  },
};
</script>

<style scoped>
/* Downloads */
.dl {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 30px;
}

.dl-num {
  font-size: clamp(64px, 7vw, 96px);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1;
}

.dl-sub { color: var(--text-dim); font-size: 16px; margin: 6px 0 22px; }
.dl-stores { display: flex; gap: 10px; }
.dl-stores .chip { padding: 8px 14px; }

/* Screens */
.screens-card { min-height: 460px; }

.screens {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -30px;
  height: 290px;
}

.mini-phone {
  position: absolute;
  bottom: 0;
  width: 230px;
  height: 290px;
  padding: 22px 16px;
  border-radius: 32px 32px 0 0;
  background: #0b0b0d;
  border: 5px solid #2a2a2f;
  border-bottom: none;
}

.mini-phone.back {
  left: 50%;
  transform: translateX(-95%) rotate(-6deg);
  transform-origin: bottom right;
  opacity: 0.7;
}

.mini-phone.front {
  left: 50%;
  transform: translateX(-5%) rotate(4deg);
  transform-origin: bottom left;
  box-shadow: 0 -10px 60px rgba(59, 130, 246, 0.18);
}

.mp-h { font-size: 20px; font-weight: 700; margin-bottom: 12px; }

.sched-row,
.grade-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 13px;
}

.period {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: rgba(59, 130, 246, 0.18);
  color: #93c5fd;
  font-size: 12px;
  font-weight: 700;
}

.sched-body strong { display: block; font-weight: 500; }
.sched-body small { color: var(--text-faint); font-size: 11px; }

.grade-name { flex: 1; }
.grade-pct { color: var(--text-dim); font-size: 12px; }

.grade-letter {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
}

/* Award */
.award-card {
  display: flex;
  align-items: center;
  gap: 28px;
  min-height: 0;
  padding: 32px;
  background:
    radial-gradient(500px circle at 0% 50%, rgba(59, 130, 246, 0.14), transparent 60%),
    linear-gradient(180deg, var(--card-top), var(--card-bottom));
}

.award-medal {
  display: grid;
  place-items: center;
  flex: none;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fef3c7, #f59e0b 50%, #92400e);
  box-shadow: 0 0 40px rgba(245, 158, 11, 0.35), inset 0 -6px 14px rgba(0, 0, 0, 0.3);
  font-size: 34px;
  color: #fff;
}

.award-copy h3 { margin-bottom: 6px; }
.award-copy p { max-width: 60ch; }
.award-dates { margin-left: auto; color: var(--text-faint); white-space: nowrap; }

@media (max-width: 900px) {
  .award-card { flex-direction: column; align-items: flex-start; }
  .award-dates { margin-left: 0; }
  .mini-phone { width: 200px; }
}
</style>
